// Foundations
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

// rxjs
import { finalize, map } from 'rxjs/operators';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';
import { CognitoService } from 'src/app/services/cognito.service';
import { IsEmailExistGQL } from 'src/app/services/graphql.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { RegisterService } from 'src/app/services/register.service';
import { MyProfileService } from '../../../services/my-profile.service';

// Components
import { StartOverDialogComponent } from '../../register/start-over-dialog/start-over-dialog.component';
import { ConfirmDialogComponent } from '../../_share/confirm-dialog/confirm-dialog.component';

const PHONE_REGEX = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

@Component({
  selector: 'app-sign-in-basic-info',
  templateUrl: './sign-in-basic-info.component.html',
  styleUrls: ['./sign-in-basic-info.component.scss']
})
export class SignInBasicInfoComponent implements OnInit {

  form: FormGroup;

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private router: Router,
    public myProfileService: MyProfileService,
    private registerService: RegisterService,
    private cognitoService: CognitoService,
    private isEmailExistQuery: IsEmailExistGQL,
    private _analytics: AnalyticsService,
    private loadingIndicatorService: LoadingIndicatorService,
  ) {
    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      phone: this.fb.group({
        id: null,
        phoneNumber: ['', [Validators.required, Validators.pattern(PHONE_REGEX)]],
        phoneType: this.myProfileService.phoneTypeList[0]
      })
    });
  }

  ngOnInit(): void {
    this._analytics.logScreen('signInBasicInfoPage');
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f(): any {
    return this.form.controls;
  }

  onContinue(): void {
    this._analytics.logButtonTap('signInBasicInfo_continue');

    const { firstName, lastName, phone } = this.form.value;
    this.registerService.updateRegisterFormData({ firstName, lastName, phone });

    const { email, password } = this.registerService.readRegisterFormData();

    this.loadingIndicatorService.show();

    this.cognitoService.signIn({ username: email, password }).subscribe(status => {
      if (status.result === 'SUCCESS') {
        // "Email Check"
        this.isEmailExistQuery.fetch({ email })
          .pipe(
            map((res) => res.data.isEmailExist),
            finalize(() => {
              this.loadingIndicatorService.hide();
              this.cognitoService.signOut();
            })
          )
          .subscribe(isEmailExist => {
            if (isEmailExist) {
              this.router.navigate(['/register/location']);
            } else {
              // if email does not exist,
              const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                panelClass: 'general-dialog',
                data: {
                  title: 'Email Not Found',
                  content: `Oops, it looks like we don't have your email address in our system. Are you new to Camp Bow Wow®?`,
                  cancelText: 'No',
                  cancelFn: () => {
                    // user chooses "No", open start over prompt dialog
                    dialogRef.close();
                    this.openStartOverDialog();
                  },
                  okText: 'Yes',
                  okFn: () => {
                    // user chooses "Yes", navigate to location selection
                    dialogRef.close();
                    this.router.navigate(['/register/location']);
                  }
                },
                autoFocus: false,
                disableClose: true,
              });
            }
          });
      } else {
        this.loadingIndicatorService.hide();
        this.snackBar.open(status.message, '', { duration: 2000 });
      }
    });
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private openStartOverDialog() {
    this._analytics.logScreen('regStartOverPage');

    const dialogRef = this.dialog.open(StartOverDialogComponent, {
      panelClass: 'general-dialog',
      data: {
        startOverFn: () => {
          this._analytics.logButtonTap('regStartOver_startOver');
          dialogRef.close();
          this.router.navigate(['/register/info']);
        },
        contactCbwFn: () => {
          this._analytics.logButtonTap('regStartOver_contactLocalCBW');
        }
      },
      autoFocus: false,
      disableClose: true,
    });
  }
}
